import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Bell, Check, Download, KeyRound, Palette, Server, ShieldCheck, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PLANS, isLaunchOfferActive, LAUNCH_OFFER_LABEL } from "@/lib/plans";
import { SUITE, TRUST_BADGES } from "@/lib/suite";
import PlanPrice from "@/components/PlanPrice";
import LabsLink from "@/components/LabsLink";

const STATS = [
  { value: "6", label: "Connected platforms" },
  { value: "Your LLM", label: "Anthropic, OpenAI, or local" },
  { value: "Images only", label: "Customer servers, no source" },
  { value: "7 days", label: "Full-product trial" },
];

const NEW_FEATURES = [
  {
    icon: Palette,
    tag: "New",
    name: "White-label instance",
    desc: "Ship GovernX as yours — product name, logo, colors, domain, and a renamed assistant. Login and emails follow the same brand.",
  },
  {
    icon: KeyRound,
    tag: "New",
    name: "Bring your own LLM",
    desc: "Point EMIE at Anthropic, OpenAI, Azure, Groq, or Ollama. The tenant key is encrypted; HQ never needs to see it again.",
  },
  {
    icon: Server,
    tag: "New",
    name: "Click-to-deploy",
    desc: "They pull private images onto any VM or cluster they own — never the git tree, never HQ keys. Handbook at /deploy.",
    to: "/deploy",
  },
];

const TRUST_CHECKS = [
  "7-day full-platform trial",
  "No credit card required",
  "Cancel anytime",
];

const ECOSYSTEM = [
  "Salesforce",
  "Zoho CRM",
  "Zoho Books",
  "Oracle Fusion",
  "Oracle NetSuite",
  "Oracle EBS",
];

export default function Home() {
  return (
    <div data-testid="home-page">
      <section className="relative overflow-hidden">
        <div className="mx-auto grid max-w-7xl items-start gap-12 px-6 py-16 lg:grid-cols-[1.15fr_0.85fr] lg:px-8 lg:py-24">
          <div>
            <p className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[12px] font-medium text-emerald-800 dark:border-emerald-800/60 dark:bg-emerald-500/10 dark:text-emerald-300">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              Now live · White-label, your LLM, click-to-deploy
            </p>
            <h1 className="mt-5 font-display text-4xl font-extrabold leading-[1.08] text-foreground sm:text-5xl lg:text-[3.5rem]">
              Extract data.{" "}
              <span className="text-primary">Govern every limit.</span>
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
              CRM, ERP and finance in one AES-256 layer. Connect Salesforce, Zoho and Oracle,
              then run it as your product — your brand, your model, your servers — next to
              Pravadh IQ and Pravadh PM.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link to="/signup">
                <Button size="lg" variant="outline" className="h-11 rounded-lg bg-card px-5 text-sm font-semibold" data-testid="hero-cta-primary">
                  Start free — no card
                </Button>
              </Link>
              <Link to="/features" className="inline-flex items-center gap-1 text-sm font-semibold text-foreground hover:underline" data-testid="hero-cta-secondary">
                Explore the platform <ArrowRight className="h-4 w-4" />
              </Link>
              <LabsLink
                testid="hero-labs"
                className="inline-flex items-center gap-1 text-sm font-semibold text-foreground hover:underline"
              >
                Pravadh Labs
              </LabsLink>
            </div>
            <ul className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-sm text-muted-foreground">
              {TRUST_CHECKS.map((t) => (
                <li key={t} className="inline-flex items-center gap-1.5">
                  <Check className="h-4 w-4 text-emerald-600" strokeWidth={2.4} />
                  {t}
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-2xl border border-border bg-card p-6 shadow-[0_16px_50px_rgba(15,23,42,0.08)] dark:shadow-[0_16px_50px_rgba(0,0,0,0.35)]" data-testid="hero-demo-card">
            <h2 className="font-display text-xl font-bold tracking-tight">Start your workspace</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Create an account, verify your email, and connect a system in under 10 minutes.
            </p>
            <ul className="mt-5 space-y-2.5 text-sm">
              {["Connect Salesforce, Zoho or Oracle", "White-label + your own LLM", "Private images on your VPS"].map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <Link to="/signup" className="mt-6 block">
              <Button className="h-11 w-full rounded-lg text-sm font-semibold">Start free trial</Button>
            </Link>
            <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-muted-foreground">
              <span className="inline-flex items-center gap-1"><Check className="h-3.5 w-3.5 text-emerald-600" /> No spam, ever</span>
              <span className="inline-flex items-center gap-1"><Check className="h-3.5 w-3.5 text-emerald-600" /> Email verification required</span>
            </div>
          </div>
        </div>

        <div className="mx-auto max-w-7xl px-6 pb-16 lg:px-8" data-testid="stat-strip">
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            {STATS.map((s) => (
              <div key={s.label} className="border-t-2 border-primary pt-4">
                <p className="font-display text-3xl font-extrabold tracking-tight">{s.value}</p>
                <p className="mt-1 text-sm text-muted-foreground">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-border bg-card py-14" data-testid="suite-strip">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <p className="text-center text-[11px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
            The Pravadh suite
          </p>
          <div className="mt-8 grid gap-4 md:grid-cols-3">
            {SUITE.map((p) => {
              const inner = (
                <div
                  className={`h-full rounded-xl border p-5 ${
                    p.current ? "border-primary bg-primary/10" : "border-border bg-muted/40"
                  }`}
                  data-testid={`suite-card-${p.id}`}
                >
                  <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">{p.tag}</p>
                  <p className="mt-1 font-display text-lg font-bold">{p.name}</p>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{p.blurb}</p>
                </div>
              );
              return p.current ? (
                <div key={p.id}>{inner}</div>
              ) : (
                <a key={p.id} href={p.href} target="_blank" rel="noopener noreferrer">{inner}</a>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-16" data-testid="ecosystem-row">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <p className="mb-8 text-center text-[11px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
            One governance layer across your ecosystem
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            {ECOSYSTEM.map((name) => (
              <div
                key={name}
                className="rounded-lg border border-border bg-card px-4 py-2 text-sm font-semibold text-foreground/80"
                data-testid={`ecosystem-${name.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {name}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-border py-20" data-testid="whats-new-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">Just shipped</p>
          <h2 className="mt-2 font-display text-3xl font-bold tracking-tight">Run it as your product</h2>
          <p className="mt-2 max-w-2xl text-muted-foreground">
            The same GovernX your team already uses — branded for the customer, powered by their model, installed on their VPS.
          </p>
          <div className="mt-8 grid gap-4 md:grid-cols-3">
            {NEW_FEATURES.map((f) => {
              const Icon = f.icon;
              const body = (
                <>
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <Icon className="h-5 w-5" />
                    </div>
                    <span className="rounded-full bg-accent px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                      {f.tag}
                    </span>
                  </div>
                  <h3 className="mt-4 font-display text-lg font-bold">{f.name}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{f.desc}</p>
                </>
              );
              const cls = "rounded-xl border border-primary/25 bg-card p-5";
              if (f.to) {
                return (
                  <Link key={f.name} to={f.to} className={`${cls} block hover:border-primary/50`} data-testid={`new-feature-${f.name.toLowerCase().replace(/\s+/g, "-")}`}>
                    {body}
                  </Link>
                );
              }
              return (
                <div key={f.name} className={cls} data-testid={`new-feature-${f.name.toLowerCase().replace(/\s+/g, "-")}`}>
                  {body}
                </div>
              );
            })}
          </div>
          <p className="mt-6 text-sm text-muted-foreground">
            After the trial, you can still sign in and look around in read-only. Continue with a plan whenever you're ready — your data stays right here.
          </p>
        </div>
      </section>

      <section className="border-t border-border py-20" data-testid="modules-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">The platform</p>
          <h2 className="mt-2 font-display text-3xl font-bold tracking-tight">Govern, then ship it as yours.</h2>
          <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {[
              { icon: Download, name: "Extract", desc: "Pull data from every connected platform into one governed pipeline." },
              { icon: ShieldCheck, name: "Govern", desc: "Enforce policies, limits and access rules from a single control plane." },
              { icon: Bell, name: "Notify", desc: "Alerts the moment a limit is breached or a record drifts." },
              { icon: Sparkles, name: "EMIE Co-pilot", desc: "Query live limits in plain language — or swap in your own model." },
              { icon: Palette, name: "White-label", desc: "Name, logo, colors, domain, and assistant — one image, many brands." },
              { icon: Server, name: "Deploy", desc: "Private images on their VM, Kubernetes, or any OCI runtime. No source. No HQ keys.", to: "/deploy" },
            ].map((m) => {
              const Icon = m.icon;
              const inner = (
                <>
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-4 font-display text-lg font-bold">{m.name}</h3>
                  <p className="mt-1.5 text-sm text-muted-foreground">{m.desc}</p>
                </>
              );
              const cls = "rounded-xl border border-border bg-card p-5";
              if (m.to) {
                return (
                  <Link key={m.name} to={m.to} className={`${cls} block hover:border-primary/40`} data-testid={`module-card-${m.name.toLowerCase().replace(/\s+/g, "-")}`}>
                    {inner}
                  </Link>
                );
              }
              return (
                <div key={m.name} className={cls} data-testid={`module-card-${m.name.toLowerCase().replace(/\s+/g, "-")}`}>
                  {inner}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="border-t border-border bg-card py-20" data-testid="pricing-preview">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mb-10 flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">Pricing</p>
              <h2 className="mt-2 font-display text-3xl font-bold tracking-tight">Simple, monthly pricing</h2>
              <p className="mt-2 text-muted-foreground">
                {isLaunchOfferActive()
                  ? `Launch prices through ${LAUNCH_OFFER_LABEL}. Every plan includes a 7-day trial — no card required.`
                  : "Every plan includes a 7-day trial. No card required."}
              </p>
            </div>
            <Link to="/pricing">
              <Button variant="outline" className="gap-2" data-testid="pricing-preview-cta">
                Compare all plans <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {PLANS.map((p) => (
              <div
                key={p.id}
                className={`relative flex flex-col rounded-xl border bg-background p-5 ${
                  p.popular ? "border-primary shadow-md dark:shadow-none" : "border-border"
                }`}
                data-testid={`pricing-preview-${p.id}`}
              >
                {p.popular && (
                  <span className="absolute -top-2.5 left-5 rounded-full bg-accent px-2 py-0.5 text-[10px] font-bold uppercase text-white">
                    Popular
                  </span>
                )}
                <h3 className="font-display text-lg font-bold">{p.name}</h3>
                <PlanPrice plan={p} size="md" className="mt-2" />
                <p className="mt-2 text-sm text-muted-foreground">{p.blurb}</p>
                <div className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Check className="h-3.5 w-3.5 text-emerald-600" /> 7 days free
                </div>
                <Link to="/signup" className="mt-5">
                  <Button className="w-full font-semibold" variant={p.popular ? "default" : "outline"} data-testid={`pricing-preview-cta-${p.id}`}>
                    Start trial
                  </Button>
                </Link>
              </div>
            ))}
          </div>
          <div className="mt-8 flex flex-wrap justify-center gap-2">
            {TRUST_BADGES.map((b) => (
              <span key={b} className="rounded-full border border-border bg-muted px-3 py-1 text-[11px] font-medium text-muted-foreground">
                {b}
              </span>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
